import { put } from 'redux-saga/effects';
import { track } from '../../analytics/analyticsClient';
import trackEvents from '../../analytics/trackEvents';
import { animateRow } from '../../containers/Timesheet/helpers';
import api from '../../helpers/api';
import { ReduxState } from '../../types/redux/state';
import * as ClustersActions from '../Clusters/actions';
import * as TimesheetRowsActions from '../TimesheetRows/actions';
import { selectToken } from '../selectors';
import { store } from '../store';
import * as actions from './actions';

import { updateSyncStatus } from '../Timesheet/actions';

export function* createTimesheetEntryRequestSaga(payload: any): Generator<any, void, any> {
  try {
    const _store = store.getState();
    const res = yield api.send({
      url: `/timesheets/${payload.data.timesheetId}/entries`,
      method: 'POST',
      data: { ...payload.data.data },
      token: selectToken(_store),
    });
    yield put(actions.createTimesheetEntryRequestSuccess(res));
    yield put(updateSyncStatus(false));
    yield put(TimesheetRowsActions.getTimesheetRowRequest({ rowId: res.timesheetRowId, timesheetId: payload.data.timesheetId }));
    track(trackEvents.TIMESHEET_ENTRY_CREATE);
  } catch (error) {
    if (payload.data.errCb) payload.data.errCb();
    yield put(actions.createTimesheetEntryRequestFailure(error));
  } finally {
    yield put(actions.createTimesheetEntryRequestFinally(payload.data.data));
  }
}

export function* createEntryAndAssignClusterRequestSaga(payload: any): Generator<any, void, any> {
  let timesheetRowId = payload.data.data?.timesheetRowId || null;
  try {
    if (payload.data.newRow) {
      timesheetRowId = null;
    }
    const res = yield api.send({
      url: `/timesheets/${payload.data.timesheetId}/entries`,
      method: 'POST',
      data: { ...payload.data.data, timesheetRowId },
      token: selectToken(store.getState()),
    });
    yield put(actions.createEntryAndAssignClusterRequestSuccess(res));
    yield put(updateSyncStatus(false));
    yield put(
      TimesheetRowsActions.getTimesheetRowsRequest({
        timesheetId: payload.data.timesheetId,
        cb: () => {
          if (payload.data.newRow) {
            animateRow(res.timesheetRowId);
          }
        },
      }),
    );
    if (payload.data.cluster) {
      yield put(
        ClustersActions.updateClusterRequest({
          id: payload.data.cluster.id,
          data: {
            timesheet_entry_id: res.id,
            ...payload.data.cluster,
            id: undefined,
          },
        }),
      );
    }
    track(trackEvents.ACTIVITY_ASSIGN);
  } catch (error) {
    yield put(actions.createEntryAndAssignClusterRequestFailure(error));
  }
}

export function* createEntryAndAssignNodeRequestSaga(payload: any): Generator<any, void, any> {
  try {
    const _store: ReduxState = store.getState();
    const token = selectToken(_store);
    let timesheetRowId = payload.data.timesheetRowId;
    const timesheetId = payload.data.timesheetId;
    const { clusterId, nodesToRemove } = payload.data;
    if (payload.data.newRow) {
      const res = yield api.send({
        url: `/timesheets/${timesheetId}/rows`,
        method: 'POST',
        data: {},
        token,
      });
      yield put(TimesheetRowsActions.createTimesheetRowRequestSuccess(res));
      timesheetRowId = res.id;
    }
    const res = yield api.send({
      url: `/timesheets/${timesheetId}/entries`,
      method: 'POST',
      data: { ...payload.data.data, timesheetRowId },
      token,
    });
    yield put(actions.createTimesheetEntryRequestSuccess(res));
    yield put(updateSyncStatus(false));
    yield put(
      ClustersActions.splitClusterAndAssignToEntryRequest({
        id: clusterId,
        nodesToRemove,
        existingEntry: res,
        action: payload.data.action,
      }),
    );
    yield put(TimesheetRowsActions.getTimesheetRowRequest({ rowId: res.timesheetRowId, timesheetId }));
    if (payload.data.newRow) {
      animateRow(timesheetRowId);
    }
    track(trackEvents.ACTIVITY_NODE_ASSIGN);
  } catch (error) {
    yield put(actions.createEntryAndAssignNodeRequestFailure(error));
  }
}

export function* deleteTimesheetEntryRequestSaga(payload: any): Generator<any, void, any> {
  try {
    yield api.send({
      url: `/timesheets/${payload.data.timesheetId}/entries/${payload.data.entryId}`,
      method: 'DELETE',
      token: selectToken(store.getState()),
    });
    yield put(actions.deleteTimesheetEntryRequestSuccess({ entryId: payload.data.entryId }));
    yield put(updateSyncStatus(false));
    track(trackEvents.TIMESHEET_ENTRY_DELETE);
  } catch (error) {
    yield put(actions.deleteTimesheetEntryRequestFailure(error));
  }
}

export function* getTimesheetEntriesRequestSaga(payload: any): Generator<any, void, any> {
  try {
    const res = yield api.send({
      url: `/timesheets/${payload.data.timesheetId}/entries`,
      method: 'GET',
      token: selectToken(store.getState()),
    });
    yield put(actions.getTimesheetEntriesRequestSuccess(res));
  } catch (error) {
    console.error(error);
    yield put(actions.getTimesheetEntriesRequestFailure(error));
  }
}

export function* updateTimesheetEntryRequestSaga(payload: any): Generator<any, void, any> {
  try {
    const timesheetId = payload.data.timesheetId;
    const res = yield api.send({
      url: `/timesheets/${timesheetId}/entries/${payload.data.entryId}`,
      method: 'PUT',
      data: payload.data.data,
      token: selectToken(store.getState()),
    });
    yield put(actions.updateTimesheetEntryRequestSuccess(res));
    yield put(updateSyncStatus(false));
    yield put(actions.getTimesheetEntriesRequest({ timesheetId }));
    yield put(TimesheetRowsActions.getTimesheetRowRequest({ rowId: res.timesheetRowId, timesheetId }));
    track(trackEvents.TIMESHEET_ENTRY_UPDATE);
  } catch (error) {
    payload.data.errCb?.();
    yield put(actions.updateTimesheetEntryRequestFailure(error));
  } finally {
    yield put(actions.updateTimesheetEntryRequestFinally(payload.data?.entryId));
  }
}
